import {
    defaultPluralResolvers,
    type LabelTimeConfig,
    PluralForm,
    type PluralFormConfig,
    type PluralHandler,
    type PluralResolvers,
    type SeparatorConfig,
    TIME_MULTIPLIERS,
    TIME_UNITS,
    type TimeUnit,
    UNIT_SUFFIX,
} from './timeConfig';

interface TimeValue {
    unit: TimeUnit;
    value: number;
    config: PluralFormConfig;
}

const parseFormatString = (format: string, isComposite: boolean): string => {
    const compositeMatch = format.match(/^{(.+?)}:{(.+)}$/);

    if (!compositeMatch) {
        return format;
    }

    const [, compositePart, standalonePart] = compositeMatch;

    return isComposite ? compositePart : standalonePart;
};

const replacePlaceholder = (template: string | null | undefined, value: number): string => {
    return template?.replace(/\{0\}/g, value.toString()) || '';
};

const getFormatString = (
    count: number,
    config: PluralFormConfig,
    pluralHandler: PluralHandler,
    isComposite: boolean
): string => {
    const pluralForm = pluralHandler(count);
    let format = config[pluralForm];

    // Fallback to 'other' if the specific form is not available or null
    if (format === null || format === undefined) {
        format = config[PluralForm.OTHER] || '';
    }

    if (!format) {
        return '';
    }

    const parsedFormat = parseFormatString(format, isComposite);

    return replacePlaceholder(parsedFormat, count);
};

const calculateTimeValues = (timeInSeconds: number, config: LabelTimeConfig): TimeValue[] => {
    if (timeInSeconds <= 0) {
        return [];
    }

    const availableUnits = TIME_UNITS.filter((unit) => {
        const configKey = `${unit}${UNIT_SUFFIX}` as keyof LabelTimeConfig;

        return config[configKey] !== undefined;
    });

    const timeValues: TimeValue[] = [];
    let remainingSeconds = Math.round(timeInSeconds);

    for (const unit of availableUnits) {
        const configKey = `${unit}${UNIT_SUFFIX}` as keyof LabelTimeConfig;
        const unitConfig = config[configKey];

        if (!unitConfig) continue;

        const multiplier = TIME_MULTIPLIERS[unit.toUpperCase() as Uppercase<TimeUnit>];
        const value = Math.floor(remainingSeconds / multiplier);

        if (value > 0) {
            timeValues.push({
                unit,
                value,
                config: unitConfig,
            });

            remainingSeconds -= value * multiplier;
        }
    }

    return timeValues;
};

const getSmallestUnit = (config: LabelTimeConfig): {unit: TimeUnit; config: PluralFormConfig} | null => {
    const reversedUnits = [...TIME_UNITS].reverse();

    for (const unit of reversedUnits) {
        const configKey = `${unit}${UNIT_SUFFIX}` as keyof LabelTimeConfig;
        const unitConfig = config[configKey];

        if (unitConfig) {
            return {unit, config: unitConfig};
        }
    }

    return null;
};

export const formatTime = (
    timeInSeconds: number,
    config: LabelTimeConfig,
    language: string,
    separators?: SeparatorConfig,
    customResolvers?: PluralResolvers
): string => {
    const handlers: PluralResolvers = {...defaultPluralResolvers, ...customResolvers};
    const pluralHandler = handlers[language.toLowerCase()];

    if (!pluralHandler) {
        throw new Error(`No plural resolver found for language: ${language}`);
    }

    // Handle zero or negative time
    if (timeInSeconds <= 0) {
        const smallestUnit = getSmallestUnit(config);

        if (!smallestUnit) {
            return '';
        }

        return getFormatString(0, smallestUnit.config, pluralHandler, false);
    }

    const timeValues = calculateTimeValues(timeInSeconds, config);

    if (timeValues.length === 0) {
        return '';
    }

    // Check if this is a composite period (more than one unit)
    const isComposite = timeValues.length > 1;

    const formattedParts = timeValues
        .map(({value, config: unitConfig}) => {
            return getFormatString(value, unitConfig, pluralHandler, isComposite);
        })
        .filter((part) => part.length > 0);

    if (!isComposite) {
        return formattedParts[0] || '';
    }

    const separator = separators?.compositeSeparator ?? ', ';
    const exceptLast = formattedParts.slice(0, -1).join(separator);
    const lastPart = formattedParts[formattedParts.length - 1];

    return `${exceptLast}${separators?.standaloneSeparator ?? separator}${lastPart}`;
};

export {defaultPluralResolvers, PluralForm};
